import { IconBook } from 'central-icons/IconBook';
import { IconClipboard } from 'central-icons/IconClipboard';
import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import { IconExpand } from 'central-icons/IconExpand';
import { IconFilter1 } from 'central-icons/IconFilter1';
import { IconHashtag } from 'central-icons/IconHashtag';
import { IconMinimize } from 'central-icons/IconMinimize';
import { IconTrashCan } from 'central-icons/IconTrashCan';
import classNames from 'classnames';
import {
  type MouseEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { Button, IconButton, RevelioTooltip } from '@revelio/ui';

import { CopyAsMenu } from '../CopyAsMenu';
import { graphqlEndpoint } from '../graphql-endpoint';
import { healthCheckPlaygroundLink } from '../health-check-link';
import { graphqlRequestSpec } from '../request-snippets';
import { copyTextToClipboard } from '../selection-lists/components/SelectionListSelect/utils';
import {
  getChartDrawTimes,
  getChartItemIdsForRequest,
} from './chart-request-registry';
import {
  type InspectorRecord,
  isInspectorActive,
  MAX_HEIGHT,
  MAX_WIDTH,
  MIN_HEIGHT,
  MIN_WIDTH,
  type PageLoadTiming,
  useDataInspectorStore,
  type VisitRequest,
} from './data-inspector.store';
import styles from './data-inspector-drawer.module.css';
import { trackDataInspectorUsed } from './data-inspector-tracking';
import {
  diffEntityRefs,
  type EntityRef,
  groupRefsByCategory,
  MAX_REFS,
  resolvePayloadAtPath,
} from './extract-entity-refs';
import {
  type FilterSelectionPart,
  formatFilterSelection,
} from './filter-selection-summary';
import {
  buildPhases,
  buildRecordBars,
  longestRequest,
  type Phase,
  spanOfRecords,
  type TimelineRecord,
  type TimelineSpan,
} from './page-timeline';
import {
  assessResponseUsage,
  type CategoryUsage,
  type ResponseUsage,
} from './response-usage';
import { skillLevelName } from './skill-level-names';
import { type RequestField, summariseRequest } from './summarise-request';
import { usePanelFrame } from './use-panel-frame';

/**
 * Ring drawn on a chart card while its request (or its category) is hovered.
 *
 * Applied as inline style rather than a class: the rule would otherwise have to
 * live behind `:global()` in a CSS module to survive hashing, and a silently
 * non-matching selector is exactly the failure mode that makes a highlight look
 * like it does nothing at all.
 */
const HIGHLIGHT_STYLE = {
  outline: '2px solid var(--ui-border-color-focus)',
  outlineOffset: '-2px',
} as const;

/**
 * `source` rings the whole card — every chart the hovered request feeds.
 * `category` draws inside it, so hovering one breakdown reads as a part of that
 * request rather than a separate thing. Different properties on purpose: both
 * can land on the same card at once and stay tellable apart.
 */
type HighlightVariant = 'source' | 'category';

const setCardHighlight = (
  anchor: Element,
  on: boolean,
  variant: HighlightVariant
) => {
  // The anchor is a `display: contents` wrapper, which has no box of its own —
  // paint the card element inside it.
  const card = anchor.firstElementChild ?? anchor;
  if (!(card instanceof HTMLElement)) return;

  if (on) {
    // Outline for both, never inset shadow — the card's own children paint over
    // an inset ring, which made the category highlight invisible. The dashed
    // inner offset reads as nested inside the solid source ring.
    card.style.outline =
      variant === 'category'
        ? '3px dashed var(--ui-border-color-focus)'
        : HIGHLIGHT_STYLE.outline;
    card.style.outlineOffset =
      variant === 'category' ? '-9px' : HIGHLIGHT_STYLE.outlineOffset;
  } else {
    card.style.removeProperty('outline');
    card.style.removeProperty('outline-offset');
  }
};

const highlightChartCards = (chartItemIds: string[], on: boolean) => {
  let first = true;
  for (const chartItemId of chartItemIds) {
    const card = document.querySelector(
      `[data-chart-item-id="${CSS.escape(chartItemId)}"]`
    );
    if (!card) continue;
    setCardHighlight(card, on, 'source');
    // A page is taller than the viewport, so a highlight below the fold would
    // be invisible — bring the first match up when the hover starts.
    if (on && first) {
      card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      first = false;
    }
  }
};

/**
 * Highlights the cards plotting a given breakdown — hovering `gender` lights up
 * the Gender chart. Matched on the card's own dimension rather than anything
 * the drawer knows, so it keeps working as pages add charts.
 */
const highlightChartsForCategory = (
  category: string,
  sourceChartItemIds: string[],
  on: boolean
) => {
  const categoryCards = new Set(
    document.querySelectorAll(
      `[data-chart-dimension="${CSS.escape(category)}"]`
    )
  );

  // The rest of the request's cards keep the source ring, so the category still
  // reads as nested inside its data source. A card in both sets gets only the
  // category treatment — two outlines on one element would just overwrite.
  for (const chartItemId of sourceChartItemIds) {
    const card = document.querySelector(
      `[data-chart-item-id="${CSS.escape(chartItemId)}"]`
    );
    if (card && !categoryCards.has(card)) setCardHighlight(card, on, 'source');
  }

  for (const card of categoryCards) setCardHighlight(card, on, 'category');
};

const formatDuration = (record: InspectorRecord) => {
  if (record.status === 'pending') return 'in flight';
  if (record.status === 'cancelled') return 'cancelled';
  if (record.fromCache) return 'cache';
  if (record.durationMs === undefined) return '—';
  return `${Math.round(record.durationMs)}ms`;
};

/** Seconds past one second: the figure is read against the ms query rows. */
const formatMs = (ms: number) =>
  ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;

const formatPageLoad = (timing: PageLoadTiming) => {
  if (timing.status === 'loading') return 'rendering…';
  if (timing.status === 'abandoned') return 'left before rendered';
  return `${formatMs(timing.durationMs ?? 0)} to render`;
};

const formatBytes = (length: number) => {
  if (length < 1024) return `${length} B`;
  if (length < 1024 * 1024) return `${Math.round(length / 1024)} KB`;
  return `${(length / (1024 * 1024)).toFixed(1)} MB`;
};

const formatShare = (share: number) => `${Math.round(share * 100)}%`;

const PHASE_LABEL: Record<Phase['name'], string> = {
  shell: 'page shell',
  fetching: 'fetching',
  drawing: 'drawing',
};

/**
 * The draw a record's charts did WITH this response: the first draw at or
 * after the response landed, across the cards it feeds. A card that never
 * redrew is left out rather than pinning the bar to a stale draw.
 */
const drawnAtFor = (
  finishedAt: number | undefined,
  chartItemIds: string[]
): number | undefined => {
  if (finishedAt === undefined) return undefined;
  let latest: number | undefined;
  for (const chartItemId of chartItemIds) {
    const draw = getChartDrawTimes(chartItemId).find((at) => at >= finishedAt);
    if (draw !== undefined && (latest === undefined || draw > latest)) {
      latest = draw;
    }
  }
  return latest;
};

const toTimelineRecord = (record: InspectorRecord): TimelineRecord => ({
  id: record.id,
  label:
    record.rootFields.length > 0
      ? record.rootFields.join(', ')
      : record.operationName,
  dispatchedAt: record.dispatchedAt,
  fetchStartedAt: record.fetchStartedAt,
  finishedAt: record.finishedAt,
  drawnAt: drawnAtFor(record.finishedAt, resolveChartItemIds(record)),
  fromCache: record.fromCache,
});

const visitRequestToTimeline = (request: VisitRequest): TimelineRecord => {
  const live = getChartItemIdsForRequest(request.operationKey);
  return {
    id: request.id,
    label: request.label,
    dispatchedAt: request.dispatchedAt,
    fetchStartedAt: request.fetchStartedAt,
    finishedAt: request.finishedAt,
    drawnAt: drawnAtFor(
      request.finishedAt,
      live.length > 0 ? live : request.chartItemIds
    ),
    fromCache: request.fromCache,
  };
};

const isWithin = (span: TimelineSpan, at: number) =>
  at >= span.startedAt && at <= span.endedAt;

/** The breakdown each subscribed card plots, read off the card itself. */
const cardDimensionsFor = (chartItemIds: string[]) =>
  chartItemIds.map(
    (chartItemId) =>
      document
        .querySelector(`[data-chart-item-id="${CSS.escape(chartItemId)}"]`)
        ?.getAttribute('data-chart-dimension') ?? undefined
  );

const copyJson = (value: unknown) =>
  copyTextToClipboard(JSON.stringify(value, null, 2));

const describeCategoryUsage = (usage: CategoryUsage) => {
  const plural = usage.usedByCards === 1 ? '' : 's';
  if (usage.isEntityLevel) return `entity data · ${formatBytes(usage.bytes)}`;
  if (usage.isUsed) {
    return `used by ${usage.usedByCards} chart${plural} · ${formatBytes(usage.bytes)}`;
  }
  return `unused · ${formatBytes(usage.bytes)}`;
};

/**
 * Dispatch → fetch is time inside the client's own exchange pipeline — the
 * priority queue that defers low-priority operations behind the page's
 * critical ones, auth, retry — never the network.
 */
const describeQueue = (record: InspectorRecord) => {
  if (record.fromCache) return 'answered from cache';
  if (record.fetchStartedAt === undefined) return 'held in client';
  return `held in client ${formatMs(record.fetchStartedAt - record.dispatchedAt)} before the fetch`;
};

const PAGE_LOAD_TITLE =
  'Navigation → last loader cleared, the point the charts were on screen. Latest visit.';

/**
 * The public SpectaQL docs ship same-origin at `/docs`, but the link is
 * absolute so one copied out of a stage session still resolves for whoever
 * receives it.
 */
const API_DOCS_BASE_URL = 'https://terminal.reveliolabs.com/docs/';

const docsUrlForRootField = (rootField: string) =>
  `${API_DOCS_BASE_URL}#query-${rootField}`;

/**
 * A map or screener response carries hundreds of ids; rendering them all pushes
 * the copy actions off-screen, so the list is collapsed until asked for.
 */
const COLLAPSED_REF_COUNT = 24;

/** Keeps the tooltip a readable block rather than dumping a whole entity. */
const MAX_TOOLTIP_PAYLOAD_LENGTH = 600;

/** What a click copies. The chip is the only affordance, so modifiers pick. */
type CopyTarget = 'id' | 'path' | 'payload';

const serialisePayload = (payload: unknown, path: string) => {
  const resolved = resolvePayloadAtPath(payload, path);
  if (resolved === undefined) return undefined;
  try {
    return JSON.stringify(resolved, null, 2);
  } catch {
    // Cyclic or otherwise unserialisable; the path alone still helps.
    return undefined;
  }
};

/**
 * Tooltip body: the path, and the actual payload sitting at it.
 *
 * A component rather than a prebuilt string so the serialisation runs only when
 * the tooltip is actually mounted. A screener response carries hundreds of refs,
 * and stringifying every one on render would cost more than the whole drawer.
 */
const RefTooltipContent = ({
  entityRef,
  payload,
}: {
  entityRef: EntityRef;
  payload: unknown;
}) => {
  const json = serialisePayload(payload, entityRef.path);
  const shown =
    json !== undefined && json.length > MAX_TOOLTIP_PAYLOAD_LENGTH
      ? `${json.slice(0, MAX_TOOLTIP_PAYLOAD_LENGTH)}\n… truncated — shift-click to copy it all`
      : json;

  return (
    <div className={styles.refTooltip}>
      <div className={styles.refTooltipPath}>{entityRef.path}</div>
      {shown !== undefined && (
        <pre className={styles.refTooltipPayload}>{shown}</pre>
      )}
      <div className={styles.refTooltipHint}>
        click: id · alt-click: path
        {shown === undefined ? '' : ' · shift-click: payload'}
      </div>
    </div>
  );
};

/** Clicking a chip copies; the label swaps to confirm which thing landed. */
const RefChip = ({
  entityRef,
  payload,
}: {
  entityRef: EntityRef;
  payload: unknown;
}) => {
  const [copied, setCopied] = useState<CopyTarget | null>(null);
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    },
    []
  );

  const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
    const pick = (): CopyTarget => {
      if (event.shiftKey) return 'payload';
      if (event.altKey) return 'path';
      return 'id';
    };
    const target = pick();

    // The full payload, not the tooltip's truncated preview — truncation is a
    // display concern and a clipped JSON blob would be useless to paste.
    const read = () => {
      if (target === 'id') return entityRef.id;
      if (target === 'path') return entityRef.path;
      return serialisePayload(payload, entityRef.path);
    };
    const text = read();
    if (text === undefined) return;

    const didCopy = await copyTextToClipboard(text);
    if (!didCopy) return;
    setCopied(target);
    if (copyTimeoutRef.current !== null) {
      window.clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = window.setTimeout(() => setCopied(null), 1200);
  };

  return (
    <RevelioTooltip
      label={<RefTooltipContent entityRef={entityRef} payload={payload} />}
      placement="top"
      textAlign="left"
      maxWidth={420}
      isInteractive
    >
      <button
        type="button"
        className={classNames(styles.ref, copied !== null && styles.refCopied)}
        onClick={(event) => void handleCopy(event)}
      >
        {copied !== null ? (
          `copied ${copied}`
        ) : (
          <>
            {entityRef.label ? `${entityRef.label} ` : ''}
            <span className={styles.refId}>{entityRef.id}</span>
          </>
        )}
      </button>
    </RevelioTooltip>
  );
};

/**
 * The response size, filled to the share of it the page's charts plot. Plain
 * when nothing in the response could be attributed — a size alone is still
 * worth showing, a 100% fill would not be.
 */
const SizeChip = ({
  bytes,
  usage,
}: {
  bytes: number;
  usage: ResponseUsage | undefined;
}) => {
  const isAttributed =
    usage !== undefined && usage.usedBytes + usage.unusedBytes > 0;
  return (
    <span
      className={classNames(styles.badge, styles.sizeChip)}
      title={
        isAttributed
          ? `${formatBytes(bytes)} response · ${formatShare(usage.usedShare)} of it plotted by this page's charts`
          : `${formatBytes(bytes)} response (JSON)`
      }
      data-testid="data-inspector-size"
    >
      {isAttributed && (
        <span
          className={styles.sizeChipFill}
          style={{ width: formatShare(usage.usedShare) }}
        />
      )}
      <span className={styles.sizeChipLabel}>{formatBytes(bytes)}</span>
    </span>
  );
};

/** Copies the `x-request-id`, the handle Kibana's tracing view is keyed on. */
const CopyRequestIdButton = ({
  requestId,
  rootField,
}: {
  requestId: string;
  rootField: string;
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    },
    []
  );
  const label = isCopied ? 'Request id copied' : 'Copy request id';
  return (
    <IconButton
      aria-label={label}
      title={`${label} — ${requestId}`}
      icon={<IconHashtag size={13} />}
      size="sm"
      shape="squared"
      variant="ghost"
      isActive={isCopied}
      className={classNames(
        styles.copyRequestId,
        isCopied && styles.copyRequestIdCopied
      )}
      onClick={() => {
        void copyTextToClipboard(requestId).then((copied) => {
          if (!copied) return;
          trackDataInspectorUsed('copied_request_id', {
            root_field: rootField,
          });
          setIsCopied(true);
          if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = window.setTimeout(
            () => setIsCopied(false),
            1200
          );
        });
      }}
    />
  );
};

const RefList = ({
  refs,
  payload,
}: {
  refs: EntityRef[];
  payload: unknown;
}) => {
  const [showAll, setShowAll] = useState(false);

  if (refs.length === 0) {
    return <span className={styles.count}>none found</span>;
  }

  const visible = showAll ? refs : refs.slice(0, COLLAPSED_REF_COUNT);

  return (
    <div className={styles.refs}>
      {visible.map((ref) => (
        <RefChip
          key={`${ref.path}:${ref.id}`}
          entityRef={ref}
          payload={payload}
        />
      ))}
      {refs.length > COLLAPSED_REF_COUNT && (
        <button
          type="button"
          className={styles.ref}
          onClick={() => setShowAll((all) => !all)}
        >
          {showAll
            ? 'show fewer'
            : `+${refs.length - COLLAPSED_REF_COUNT} more`}
        </button>
      )}
    </div>
  );
};

/** `(500)` from a capped walk would otherwise read as an exact count. */
const formatRefCount = (refs: EntityRef[]) =>
  refs.length >= MAX_REFS ? `${MAX_REFS}+, capped` : String(refs.length);

/**
 * Entities split by what they are — gender, role, geography — rather than one
 * flat run of ids. Hovering a category lights up the charts plotting it.
 */
const CategorisedRefs = ({
  refs,
  sourceChartItemIds,
  payload,
  usage,
}: {
  refs: EntityRef[];
  sourceChartItemIds: string[];
  /** The side of the exchange these refs came from, for the chip tooltips. */
  payload: unknown;
  /** Response side only: which categories the page plots, and their size. */
  usage?: ResponseUsage;
}) => {
  const groups = useMemo(() => groupRefsByCategory(refs), [refs]);

  if (groups.length === 0) {
    return <span className={styles.count}>none found</span>;
  }

  const usageOf = (category: string) =>
    usage?.categories.find((entry) => entry.category === category);
  const used = groups.filter(
    (group) => usageOf(group.category)?.isUsed !== false
  );
  const unused = groups.filter(
    (group) => usageOf(group.category)?.isUsed === false
  );

  const renderGroup = (group: (typeof groups)[number]) => {
    const categoryUsage = usageOf(group.category);
    return (
      <div
        key={group.category}
        className={styles.category}
        onMouseEnter={() =>
          highlightChartsForCategory(group.category, sourceChartItemIds, true)
        }
        onMouseLeave={() =>
          highlightChartsForCategory(group.category, sourceChartItemIds, false)
        }
      >
        <div className={styles.categoryHead}>
          {group.category}
          <span className={styles.count}>{group.refs.length}</span>
          {categoryUsage && (
            <span className={styles.categoryUsage}>
              {describeCategoryUsage(categoryUsage)}
            </span>
          )}
        </div>
        <RefList refs={group.refs} payload={payload} />
      </div>
    );
  };

  return (
    <div className={styles.categories}>
      {used.map(renderGroup)}
      {unused.length > 0 && (
        <div className={styles.unused}>
          <CollapsibleSection
            title="Unused by this page"
            count={
              usage ? formatBytes(usage.unusedBytes) : String(unused.length)
            }
            defaultOpen={false}
          >
            {unused.map(renderGroup)}
          </CollapsibleSection>
        </div>
      )}
    </div>
  );
};

/** One summarised request field: a label and either chips or a value. */
const RequestFieldRow = ({
  field,
  refs,
  payload,
}: {
  field: RequestField;
  refs: EntityRef[];
  payload: unknown;
}) => {
  const fieldRefs = useMemo(
    () =>
      field.isIdList
        ? refs.filter((ref) => ref.path.startsWith(field.path))
        : [],
    [field, refs]
  );
  return (
    <div className={styles.requestField}>
      <span className={styles.requestFieldLabel} title={field.key}>
        {field.label}
      </span>
      {field.isIdList ? (
        <RefList refs={fieldRefs} payload={payload} />
      ) : (
        <span className={styles.requestFieldValue}>{field.display}</span>
      )}
    </div>
  );
};

/**
 * Request and response each collapse. Requested starts closed: it is usually a
 * single entity you already know, while the response is what you opened the row
 * to read.
 */
const CollapsibleSection = ({
  title,
  count,
  defaultOpen,
  onCopy,
  children,
}: {
  title: string;
  count: string;
  defaultOpen: boolean;
  /** Copies the payload this section describes. */
  onCopy?: () => void;
  children: ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeadRow}>
        <button
          type="button"
          className={styles.sectionToggle}
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
        >
          <span className={styles.sectionCaret}>{isOpen ? '▾' : '▸'}</span>
          {title} ({count})
        </button>
        {onCopy && (
          <IconButton
            aria-label={`Copy ${title.toLowerCase()} as JSON`}
            title={`Copy ${title.toLowerCase()} as JSON`}
            icon={<IconClipboard size={13} />}
            size="sm"
            shape="squared"
            variant="ghost"
            onClick={onCopy}
          />
        )}
      </div>
      {isOpen && children}
    </div>
  );
};

/**
 * The request as a bar on its group's timeline: where it was dispatched,
 * how long it queued before the fetch, the round-trip, and the draw after.
 */
const RecordTimeline = ({
  record,
  span,
  navigationAt,
}: {
  record: InspectorRecord;
  span: TimelineSpan;
  navigationAt: number | null;
}) => {
  const timelineRecord = toTimelineRecord(record);
  const [bar] = buildRecordBars(span, [timelineRecord]);
  if (!bar) return null;
  const pct = (fraction: number) => `${(fraction * 100).toFixed(2)}%`;
  const parts = [
    `dispatched +${formatMs(record.dispatchedAt - span.startedAt)}${
      navigationAt === null
        ? ''
        : ` (${formatMs(record.dispatchedAt - navigationAt)} after navigation)`
    }`,
    describeQueue(record),
    record.durationMs === undefined
      ? ''
      : `network ${formatMs(record.durationMs)}`,
    timelineRecord.drawnAt !== undefined && record.finishedAt !== undefined
      ? `drawn ${formatMs(timelineRecord.drawnAt - record.finishedAt)} after`
      : '',
  ].filter((part) => part.length > 0);

  return (
    <div
      className={styles.recordTimeline}
      title={parts.join(' · ')}
      data-dispatched-at={Math.round(record.dispatchedAt)}
      data-finished-at={
        record.finishedAt === undefined
          ? undefined
          : Math.round(record.finishedAt)
      }
      data-span={`${Math.round(span.startedAt)}-${Math.round(span.endedAt)}`}
    >
      <span
        className={classNames(
          styles.barQueued,
          bar.fromCache && styles.barCache
        )}
        style={{ left: pct(bar.left), width: pct(bar.queued) }}
      />
      <span
        className={classNames(
          styles.barNetwork,
          bar.isPending && styles.barPending
        )}
        style={{ left: pct(bar.left + bar.queued), width: pct(bar.network) }}
      />
      <span
        className={styles.barDrawn}
        style={{
          left: pct(bar.left + bar.queued + bar.network),
          width: pct(bar.drawn),
        }}
      />
    </div>
  );
};

/**
 * Resolved live rather than read off the record, because subscribers join
 * AFTER the operation fires. Transitions is the clearest case: the Sankey and
 * both diverging-bar cards share one `QueryTransitionsV3` response — the
 * breakdown is applied client-side per card — so urql dedupes them onto one
 * operation, but only the first card had registered by the time it was
 * captured. A snapshot would highlight that one card and miss the rest.
 */
const resolveChartItemIds = (record: InspectorRecord) => {
  const live = getChartItemIdsForRequest(record.operationKey);
  return live.length > 0 ? live : record.chartItemIds;
};

/**
 * Opens the health-check playground on the check for this root field — the
 * tab for the page that sent it, when the board has one — loaded with the
 * request the page actually sent: the jump from "this is what was asked for"
 * to running it yourself and changing it.
 *
 * Rendered inert when nothing on this environment's board probes the field:
 * mutations (the sweep is read-only), admin-screen queries, the few ops
 * needing an id from an immediately preceding request, and off prod the ops
 * only a deprecated check probes. The drawer and the board share one audience
 * (`useIsRealRevelioAdmin`), so a live link always opens something.
 */
const PlaygroundLink = ({
  rootField,
  query,
  variables,
  route,
}: {
  rootField: string;
  query: string;
  variables: Record<string, unknown>;
  route: string;
}) => {
  const href = healthCheckPlaygroundLink(
    rootField,
    { query, variables },
    route
  );
  if (href === null) {
    return (
      <Button
        size="xs"
        variant="outlined"
        disabled
        title={`No health check on this environment's board probes ${rootField}, so there is nothing to open in the playground`}
      >
        playground
      </Button>
    );
  }
  return (
    // `asChild` so the anchor keeps link semantics (open in a new tab, copy
    // link address) while wearing the same button as the actions beside it.
    <Button size="xs" variant="outlined" asChild>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={`Open ${rootField} in the health-check playground with this request`}
        onClick={() =>
          trackDataInspectorUsed('opened_playground', { root_field: rootField })
        }
      >
        playground ↗
      </a>
    </Button>
  );
};

const RecordRow = ({
  record,
  span,
  navigationAt,
}: {
  record: InspectorRecord;
  /** The timeline the row's bar is drawn against. */
  span: TimelineSpan | null;
  /** When the visit these requests belong to was navigated to, if known. */
  navigationAt: number | null;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const setFocusedChart = useDataInspectorStore(
    (state) => state.setFocusedChart
  );

  const diff = useMemo(
    () => diffEntityRefs(record.variables, record.data),
    [record.variables, record.data]
  );
  const chartItemIds = resolveChartItemIds(record);
  const focusedChartItemId = useDataInspectorStore(
    (state) => state.focusedChartItemId
  );
  const isFocused =
    focusedChartItemId !== null && chartItemIds.includes(focusedChartItemId);

  const requestGroups = useMemo(
    () => summariseRequest(record.variables, skillLevelName),
    [record.variables]
  );
  const requestRefs = useMemo(
    () => [...diff.requested, ...diff.modifiers],
    [diff]
  );
  // The card attribute is read live: cards register after the operation
  // fires. Keyed on the joined ids because the array is rebuilt every render.
  const chartItemIdsKey = chartItemIds.join('|');
  // biome-ignore lint/correctness/useExhaustiveDependencies: chartItemIds is keyed by chartItemIdsKey
  const usage = useMemo(
    () =>
      record.data !== undefined
        ? assessResponseUsage({
            groups: groupRefsByCategory(diff.returned),
            payload: record.data,
            cardDimensions: cardDimensionsFor(chartItemIds),
          })
        : undefined,
    [record.data, diff.returned, chartItemIdsKey]
  );
  const usedShareLabel =
    usage !== undefined && usage.usedBytes + usage.unusedBytes > 0
      ? ` · ${formatShare(usage.usedShare)} used`
      : '';

  // `GetPayV3` is a frontend-only handle. The field it selects — `pay_v3` — is
  // the name the API docs, the backend team, and clients all use.
  const rootFieldLabel =
    record.rootFields.length > 0
      ? record.rootFields.join(', ')
      : record.operationName;

  return (
    <div
      className={styles.record}
      onMouseEnter={() =>
        highlightChartCards(resolveChartItemIds(record), true)
      }
      onMouseLeave={() =>
        highlightChartCards(resolveChartItemIds(record), false)
      }
    >
      <div className={styles.recordHeader}>
        <button
          type="button"
          className={styles.recordToggle}
          onClick={() => setIsExpanded((expanded) => !expanded)}
          aria-expanded={isExpanded}
          data-testid="data-inspector-record"
        >
          <span
            className={styles.opName}
            // The GraphQL field is what the row is named after; the document
            // name stays one hover away so an FE dev can still tie the row back
            // to its query source and its `expectLastEventually` in the specs.
            title={`${record.operationName} — frontend query name`}
          >
            {rootFieldLabel}
          </span>
        </button>

        {/* Siblings of the toggle, never children: nested buttons are invalid,
            and a click would expand the row as well as act. Revealed on hover
            (see the stylesheet) so a long list stays quiet to read. */}
        {record.requestId !== undefined && (
          <CopyRequestIdButton
            requestId={record.requestId}
            rootField={rootFieldLabel}
          />
        )}

        {/* Widens the toggle's click target to the whole row; the button above
            is the accessible control. */}
        <span
          className={styles.recordBadges}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          {chartItemIds.length > 1 && (
            <span
              className={styles.badge}
              title={`Feeds ${chartItemIds.length} charts on this page`}
            >
              {chartItemIds.length} charts
            </span>
          )}
          {record.status === 'error' && (
            <span className={classNames(styles.badge, styles.badgeError)}>
              error
            </span>
          )}
          {record.responseBytes !== undefined && (
            <SizeChip bytes={record.responseBytes} usage={usage} />
          )}
          <span className={styles.badge}>{formatDuration(record)}</span>
          {record.cacheReads !== undefined && record.cacheReads > 0 && (
            <span
              className={styles.badge}
              title="Re-read from the client cache this many times since the fetch shown — the page did not fetch it again"
            >
              cache ×{record.cacheReads}
            </span>
          )}
        </span>

        {chartItemIds[0] && (
          <IconButton
            aria-label={
              isFocused ? 'Show all requests' : 'Show only this chart'
            }
            title={
              isFocused
                ? 'Showing only this chart — click to show all requests'
                : 'Hide every request except this chart'
            }
            icon={<IconFilter1 size={14} />}
            size="sm"
            shape="squared"
            variant="ghost"
            isActive={isFocused}
            className={classNames(
              styles.filterChart,
              isFocused && styles.filterChartActive
            )}
            onClick={() =>
              setFocusedChart(isFocused ? null : (chartItemIds[0] ?? null))
            }
          />
        )}
      </div>

      {span && (
        <RecordTimeline
          record={record}
          span={span}
          navigationAt={navigationAt}
        />
      )}

      {isExpanded && (
        <div className={styles.body}>
          {record.error && <div className={styles.error}>{record.error}</div>}

          {/* A toolbar above the payload it acts on. The sections below run to
              dozens of refs, which left these stranded past a long scroll. */}
          <div className={styles.actions}>
            {/* Supersedes a plain "Copy request": that button's JSON body is
                now the menu's Raw JSON body entry, beside runnable curl /
                Python / Node snippets. Session-cookie auth, not the board's
                bearer — this request was authenticated by the reader's own
                session, and a bearer snippet would 401 for a non-admin. */}
            <CopyAsMenu
              spec={graphqlRequestSpec({
                url: graphqlEndpoint(),
                query: record.query,
                variables: record.variables,
                auth: 'session-cookie',
              })}
              onCopied={(format) =>
                trackDataInspectorUsed('copied_request', {
                  root_field: rootFieldLabel,
                  snippet_format: format,
                })
              }
            />
            {/* The two that leave the drawer sit apart from the two that act
                on the record in place. */}
            <div className={styles.actionsRight}>
              {record.rootFields.map((rootField) => (
                <PlaygroundLink
                  key={`playground-${rootField}`}
                  rootField={rootField}
                  query={record.query}
                  variables={record.variables}
                  route={record.route}
                />
              ))}
              {record.rootFields.map((rootField) => (
                <Button key={rootField} size="xs" variant="outlined" asChild>
                  <a
                    href={docsUrlForRootField(rootField)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open the ${rootField} API documentation`}
                    title={`Open the ${rootField} API documentation`}
                  >
                    <IconBook size={13} />
                  </a>
                </Button>
              ))}
            </div>
          </div>

          <CollapsibleSection
            title="Request"
            count={formatRefCount(diff.requested)}
            defaultOpen={false}
            onCopy={() => void copyJson(record.variables)}
          >
            <div className={styles.requestGroups}>
              {requestGroups.map((group) => (
                <div key={group.title} className={styles.requestGroup}>
                  <div className={styles.sectionHead}>{group.title}</div>
                  {group.title === 'Entities' ? (
                    <CategorisedRefs
                      refs={diff.requested}
                      sourceChartItemIds={chartItemIds}
                      payload={record.variables}
                    />
                  ) : (
                    group.fields.map((field) => (
                      <RequestFieldRow
                        key={field.path}
                        field={field}
                        refs={requestRefs}
                        payload={record.variables}
                      />
                    ))
                  )}
                </div>
              ))}
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Response"
            count={`${formatRefCount(diff.returned)}${usedShareLabel}`}
            defaultOpen={true}
            onCopy={() => {
              // Counted only once the copy landed, like copied_request —
              // a refused clipboard permission is not a copy.
              void copyJson(record.data).then((copied) => {
                if (!copied) return;
                trackDataInspectorUsed('copied_response', {
                  root_field: rootFieldLabel,
                });
              });
            }}
          >
            <CategorisedRefs
              refs={diff.returned}
              sourceChartItemIds={chartItemIds}
              payload={record.data}
              usage={usage}
            />
          </CollapsibleSection>
        </div>
      )}
    </div>
  );
};

type FilterGroup = {
  signature: string;
  parts: FilterSelectionPart[];
  records: InspectorRecord[];
};
type PageGroup = { route: string; filterGroups: FilterGroup[] };

/**
 * Nests records page → filter selection, preserving the newest-first order the
 * store keeps. Insertion order carries that through: the first time a route (or
 * a selection within it) is seen, it is the most recent one, so groups sort
 * themselves without a timestamp comparison.
 */
const groupRecordsByPageAndFilter = (
  records: InspectorRecord[]
): PageGroup[] => {
  const pages = new Map<string, Map<string, FilterGroup>>();

  for (const record of records) {
    const byFilter = pages.get(record.route) ?? new Map<string, FilterGroup>();
    if (!pages.has(record.route)) pages.set(record.route, byFilter);

    const existing = byFilter.get(record.filterSignature);
    if (existing) {
      existing.records.push(record);
    } else {
      byFilter.set(record.filterSignature, {
        signature: record.filterSignature,
        // Labels sharpen as selection lists resolve, so the newest record's
        // parts are the most resolved ones — and it lands first.
        parts: record.filterParts,
        records: [record],
      });
    }
  }

  return [...pages.entries()].map(([route, byFilter]) => ({
    route,
    filterGroups: [...byFilter.values()],
  }));
};

/**
 * One filter selection's requests. Collapsed unless it is the selection
 * currently in force — on a page you have been changing filters on, every
 * previous selection is history you scan, not history you read.
 */
const FilterGroupSection = ({
  group,
  isCurrent,
  visitSpan,
}: {
  group: FilterGroup;
  isCurrent: boolean;
  /** The page visit's timeline, when it has been measured. */
  visitSpan: TimelineSpan | null;
}) => {
  // Follows `isCurrent` rather than seeding from it: change a filter and the
  // batch you just left has to collapse on its own, or the drawer accumulates
  // every selection you have ever used, all expanded. A manual toggle still
  // holds until the current selection changes again.
  const [isOpen, setIsOpen] = useState(isCurrent);
  useEffect(() => setIsOpen(isCurrent), [isCurrent]);

  // Bars start at the group's first request: what came before it is the page
  // shell, which the render-time chip's hover accounts for, and a run of bars
  // all starting two-thirds of the way along read as a bug. The visit's own
  // requests still run to its settle, so the draw tail stays in proportion.
  const { span, navigationAt } = useMemo(() => {
    const timelineRecords = group.records.map(toTimelineRecord);
    const own = spanOfRecords(timelineRecords);
    const isVisit =
      own !== null &&
      visitSpan !== null &&
      timelineRecords.every((record) =>
        isWithin(visitSpan, record.dispatchedAt)
      );
    if (!own || !isVisit || !visitSpan) {
      return { span: own, navigationAt: null };
    }
    return {
      span: {
        startedAt: own.startedAt,
        endedAt: Math.max(own.endedAt, visitSpan.endedAt),
      },
      navigationAt: visitSpan.startedAt,
    };
  }, [group.records, visitSpan]);

  // Dispatch order, so the bars read top-down as a waterfall; the store's
  // newest-first order is for the page and filter groups.
  const orderedRecords = useMemo(
    () => [...group.records].sort((a, b) => a.dispatchedAt - b.dispatchedAt),
    [group.records]
  );

  return (
    <div className={styles.filterGroup}>
      <button
        type="button"
        className={classNames(
          styles.filterGroupHead,
          isCurrent && styles.filterGroupHeadCurrent
        )}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        title={formatFilterSelection(group.parts)}
      >
        <span className={styles.caretBox}>
          <span className={styles.sectionCaret}>{isOpen ? '▾' : '▸'}</span>
        </span>
        <span className={styles.filterChips}>
          {group.parts.length === 0 ? (
            <span className={styles.count}>No filters</span>
          ) : (
            // Indexed key: a multi-filter contributes several parts under one
            // filter name, so the name alone is not unique.
            group.parts.map((part, index) => (
              <span
                key={`${part.name}-${index}`}
                className={classNames(
                  styles.filterChip,
                  part.isPrimary && styles.filterChipPrimary
                )}
              >
                <span className={styles.filterChipName}>{part.name}</span>
                {part.value}
              </span>
            ))
          )}
        </span>
        <span className={styles.caretBox}>
          {isCurrent && <span className={styles.badge}>current</span>}
          <span className={styles.count}>{group.records.length}</span>
        </span>
      </button>
      {isOpen &&
        orderedRecords.map((record) => (
          <RecordRow
            key={record.id}
            record={record}
            span={span}
            navigationAt={navigationAt}
          />
        ))}
    </div>
  );
};

/** One route's requests, split by the filter selection each was fired under. */
const PageGroupSection = ({
  group,
  isCurrentPage,
  currentFilterSignature,
}: {
  group: PageGroup;
  isCurrentPage: boolean;
  currentFilterSignature: string;
}) => {
  // Same as the filter groups: navigating away collapses the page you left.
  const [isOpen, setIsOpen] = useState(isCurrentPage);
  useEffect(() => setIsOpen(isCurrentPage), [isCurrentPage]);

  const pageLoad = useDataInspectorStore(
    (state) => state.pageLoads[group.route]
  );

  const recordCount = group.filterGroups.reduce(
    (total, filterGroup) => total + filterGroup.records.length,
    0
  );

  // The visit runs from the navigation to the settle; while still loading it
  // runs to now, which the next record update moves along.
  const visitSpan = useMemo<TimelineSpan | null>(() => {
    if (!pageLoad || pageLoad.status === 'abandoned') return null;
    return {
      startedAt: pageLoad.startedAt,
      endedAt:
        pageLoad.status === 'rendered'
          ? pageLoad.startedAt + (pageLoad.durationMs ?? 0)
          : performance.now(),
    };
  }, [pageLoad]);

  // From the visit's own log, not the rows below: those hold the latest
  // attempt per request, and a page that re-fetches after first paint would
  // otherwise show the re-run and lose the batch the load waited on.
  const visitRecords = useMemo(
    () => (pageLoad?.requests ?? []).map(visitRequestToTimeline),
    [pageLoad]
  );
  const phases = visitSpan ? buildPhases(visitSpan, visitRecords) : [];
  const longest = longestRequest(visitRecords);

  return (
    <section className={styles.pageGroup}>
      <button
        type="button"
        className={classNames(
          styles.pageGroupHead,
          isCurrentPage && styles.pageGroupHeadCurrent
        )}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
      >
        <span className={styles.caretBox}>
          <span className={styles.sectionCaret}>{isOpen ? '▾' : '▸'}</span>
        </span>
        <span className={styles.pageGroupRoute}>
          {group.route || 'unknown'}
        </span>
        {isCurrentPage && <span className={styles.badge}>this page</span>}
        {pageLoad && (
          <RevelioTooltip
            placement="bottom"
            maxWidth={360}
            textAlign="left"
            label={
              <div className={styles.phaseTooltip}>
                <div>{PAGE_LOAD_TITLE}</div>
                {phases.map((phase) => (
                  <div key={phase.name}>
                    {PHASE_LABEL[phase.name]}: {formatMs(phase.ms)} (
                    {formatShare(phase.share)})
                  </div>
                ))}
                {longest && (
                  <div>
                    longest request: {longest.label} {formatMs(longest.ms)}
                  </div>
                )}
              </div>
            }
          >
            <span
              className={styles.badge}
              data-testid="data-inspector-page-load"
              data-span={
                visitSpan
                  ? `${Math.round(visitSpan.startedAt)}-${Math.round(visitSpan.endedAt)}`
                  : undefined
              }
            >
              {formatPageLoad(pageLoad)}
            </span>
          </RevelioTooltip>
        )}
        <span className={styles.count}>{recordCount}</span>
      </button>
      {isOpen &&
        group.filterGroups.map((filterGroup) => (
          <FilterGroupSection
            key={filterGroup.signature}
            group={filterGroup}
            isCurrent={
              isCurrentPage && filterGroup.signature === currentFilterSignature
            }
            visitSpan={visitSpan}
          />
        ))}
    </section>
  );
};

const KEYBOARD_RESIZE_STEP = 24;

type EdgeSide = 'left' | 'right' | 'bottom';

/**
 * Resize grip on one edge of the panel.
 *
 * Pointer capture rather than window listeners: the cursor always outruns a 6px
 * strip, and capture both keeps the drag alive and releases it if the pointer is
 * lost. Reports the raw client coordinate — the frame hook owns which edge stays
 * anchored, because that differs per side.
 */
const EdgeHandle = ({
  side,
  onResize,
  onNudge,
  valueNow,
  valueMin,
  valueMax,
  label,
}: {
  side: EdgeSide;
  onResize: (clientCoordinate: number) => void;
  onNudge: (delta: number) => void;
  valueNow: number;
  valueMin: number;
  valueMax: number;
  label: string;
}) => (
  <div
    className={classNames(
      styles.edgeHandle,
      side === 'left' && styles.edgeHandleLeft,
      side === 'right' && styles.edgeHandleRight,
      side === 'bottom' && styles.edgeHandleBottom
    )}
    role="separator"
    tabIndex={0}
    aria-orientation={side === 'bottom' ? 'horizontal' : 'vertical'}
    aria-label={label}
    aria-valuenow={Math.round(valueNow)}
    aria-valuemin={valueMin}
    aria-valuemax={valueMax}
    onPointerDown={(event) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
    }}
    onPointerMove={(event) => {
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
      onResize(side === 'bottom' ? event.clientY : event.clientX);
    }}
    onPointerUp={(event) =>
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    // Dragging must not be the only way to reach a control that changes layout.
    onKeyDown={(event) => {
      const grow = side === 'bottom' ? 'ArrowDown' : 'ArrowRight';
      const shrink = side === 'bottom' ? 'ArrowUp' : 'ArrowLeft';
      if (event.key === grow) onNudge(KEYBOARD_RESIZE_STEP);
      else if (event.key === shrink) onNudge(-KEYBOARD_RESIZE_STEP);
      else return;
      event.preventDefault();
    }}
  />
);

/** Stable reference for the closed drawer's record selector. */
const NO_RECORDS: InspectorRecord[] = [];

export const DataInspectorDrawer = () => {
  const enabled = useDataInspectorStore(isInspectorActive);
  const { pathname: currentRoute } = useLocation();
  const isOpen = useDataInspectorStore((state) => state.isOpen);
  const isMinimised = useDataInspectorStore((state) => state.isMinimised);
  const focusedChartItemId = useDataInspectorStore(
    (state) => state.focusedChartItemId
  );
  const currentFilterSignature = useDataInspectorStore(
    (state) => state.currentFilterSignature
  );
  // Recording runs whether or not the panel is showing, so a closed drawer must
  // not subscribe to the records themselves — it would re-render, regroup and
  // rediff on every chart request of every page for the whole session. The
  // shared empty array keeps the selected value reference-stable while closed.
  const records = useDataInspectorStore((state) =>
    state.isOpen ? state.records : NO_RECORDS
  );
  const setFrame = useDataInspectorStore((state) => state.setFrame);
  const toggleMinimised = useDataInspectorStore(
    (state) => state.toggleMinimised
  );
  const setOpen = useDataInspectorStore((state) => state.setOpen);
  const { frame, moveBy, resizeFromLeft, resizeFromRight, resizeFromBottom } =
    usePanelFrame();
  const setFocusedChart = useDataInspectorStore(
    (state) => state.setFocusedChart
  );
  const clearRecords = useDataInspectorStore((state) => state.clearRecords);

  const visibleRecords = useMemo(
    () =>
      focusedChartItemId === null
        ? records
        : // Live, not `record.chartItemIds`: that snapshot is taken while cards
          // are still mounting, so focusing a card that registered late would
          // hide the very request feeding it.
          records.filter((record) =>
            resolveChartItemIds(record).includes(focusedChartItemId)
          ),
    [records, focusedChartItemId]
  );

  const pageGroups = useMemo(
    () => groupRecordsByPageAndFilter(visibleRecords),
    [visibleRecords]
  );

  if (!enabled || !isOpen) return null;

  return (
    <aside
      className={styles.drawer}
      data-testid="data-inspector-drawer"
      style={{
        left: frame.x,
        top: frame.y,
        width: frame.width,
        // Minimised collapses to the header alone; the frame is kept so
        // restoring puts it back exactly where it was.
        height: isMinimised ? undefined : frame.height,
      }}
    >
      {!isMinimised && (
        <>
          <EdgeHandle
            side="left"
            label="Resize Data Inspector width"
            onResize={resizeFromLeft}
            onNudge={(delta) => setFrame({ width: frame.width + delta })}
            valueNow={frame.width}
            valueMin={MIN_WIDTH}
            valueMax={MAX_WIDTH}
          />
          <EdgeHandle
            side="right"
            label="Resize Data Inspector width"
            onResize={resizeFromRight}
            onNudge={(delta) => setFrame({ width: frame.width + delta })}
            valueNow={frame.width}
            valueMin={MIN_WIDTH}
            valueMax={MAX_WIDTH}
          />
          <EdgeHandle
            side="bottom"
            label="Resize Data Inspector height"
            onResize={resizeFromBottom}
            onNudge={(delta) => setFrame({ height: frame.height + delta })}
            valueNow={frame.height}
            valueMin={MIN_HEIGHT}
            valueMax={MAX_HEIGHT}
          />
        </>
      )}

      {/* The header is the drag surface. Presses that land on a control are
          left alone, or Clear/Close would be unusable. */}
      <div
        className={styles.header}
        onPointerDown={(event) => {
          if (
            event.target instanceof Element &&
            event.target.closest('button') !== null
          ) {
            return;
          }
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
          moveBy(event.movementX, event.movementY);
        }}
        onPointerUp={(event) =>
          event.currentTarget.releasePointerCapture(event.pointerId)
        }
      >
        <span className={styles.title}>Data Inspector</span>
        <span className={styles.count}>
          {visibleRecords.length}
          {focusedChartItemId === null ? '' : ` of ${records.length}`}
        </span>
        <IconButton
          aria-label={isMinimised ? 'Expand' : 'Minimise'}
          title={isMinimised ? 'Expand' : 'Minimise'}
          icon={
            isMinimised ? <IconExpand size={14} /> : <IconMinimize size={14} />
          }
          size="sm"
          shape="squared"
          variant="ghost"
          onClick={toggleMinimised}
        />
        {!isMinimised && (
          <IconButton
            aria-label="Clear captured requests"
            title="Clear captured requests"
            icon={<IconTrashCan size={14} />}
            size="sm"
            shape="squared"
            variant="ghost"
            onClick={clearRecords}
          />
        )}
        <IconButton
          aria-label="Close"
          title="Close"
          icon={<IconCrossLarge size={14} />}
          size="sm"
          shape="squared"
          variant="ghost"
          onClick={() => setOpen(false)}
        />
      </div>

      {!isMinimised && focusedChartItemId !== null && (
        <div className={styles.focusBar}>
          <span className={styles.count}>Filtered to one chart</span>
          <Button
            size="sm"
            variant="outlined"
            onClick={() => setFocusedChart(null)}
          >
            Show all
          </Button>
        </div>
      )}

      <div className={styles.list} hidden={isMinimised}>
        {visibleRecords.length === 0 ? (
          <p className={styles.empty}>
            No chart requests captured yet. Open a page with plots on it, or
            change a filter, to see the queries behind them. Requests no chart
            subscribes to — taxonomy trees, saved sets — are deliberately not
            listed.
          </p>
        ) : (
          pageGroups.map((group) => (
            <PageGroupSection
              key={group.route}
              group={group}
              isCurrentPage={group.route === currentRoute}
              currentFilterSignature={currentFilterSignature}
            />
          ))
        )}
      </div>
    </aside>
  );
};
